import { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import { useAsyncNew } from './useAsyncNew';

const createPoller = (callback, interval = 1000) => {
    let timerId = null;
    let isPolling = false;

    async function poll() {
        if (isPolling) return;
        isPolling = true;

        try {
            await callback();
        } catch (err) {
            console.error('Polling error:', err);
        } finally {
            isPolling = false;
            // Only schedule next poll if not stopped
            if (timerId !== null) {
                timerId = setTimeout(poll, interval);
            }
        }
    }

    return {
        /**
         * Starts polling and resolves with the first result.
         */
        async start() {
            if (timerId !== null) return; // already started
            timerId = 0; // mark as started (dummy value to indicate active state)
            await poll();
        },

        /**
         * Stops polling.
         */
        stop() {
            if (timerId !== null) {
                clearTimeout(timerId);
                timerId = null;
                isPolling = false;
            }
        },

        /**
         * Restarts polling and resolves with the first new result.
         */
        restart() {
            this.stop();
            return this.start();
        },

        /**
         * Returns whether the poller is currently running.
         */
        isRunning() {
            return timerId !== null;
        }
    };
}

const usePoller = (service, delayInMilliseconds = 10000, immediate = false) => {

    const asyncService = useAsyncNew(service, { immediate: false, defaultState: { status: 'pending' } });

    const pollerRef = useRef(null);

    useEffect(() => {
        pollerRef.current = createPoller(asyncService.execute, delayInMilliseconds);

        if (immediate) {
            pollerRef.current.start();
        }

        return () => {
            pollerRef.current?.stop();
        };
    }, [asyncService.execute]);


    const start = useCallback(async () => {
        return pollerRef.current?.start();
    }, []);

    const stop = useCallback(() => {
        pollerRef.current?.stop();
    }, []);

    const restart = useCallback(async () => {
        return pollerRef.current?.restart();
    }, []);

    return { data: asyncService, restart, stop, start };
};

export default usePoller;
